import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useScript } from '../context/ScriptContext';
import { useAuth } from '../context/AuthContext';
import { Order } from '../types';
import * as api from '../services/api';
import { ApiError } from '../services/api';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

const OrdersAdminPage: React.FC = () => {
  useDocumentTitle('Наруџбине — Манастир Градац');
  const { t } = useScript();
  const { isAdmin, token } = useAuth();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadOrders = useCallback(() => {
    if (!token) return;
    setLoading(true);
    setError(null);
    api.getOrders(token)
      .then(items => { setOrders(items); setLoading(false); })
      .catch((e: ApiError) => {
        setError(e?.message || 'Грешка при учитавању.');
        setLoading(false);
      });
  }, [token]);

  useEffect(() => { if (isAdmin) loadOrders(); }, [isAdmin, loadOrders]);

  const toggleStatus = async (o: Order) => {
    if (!token) return;
    const next = o.status === 'done' ? 'new' : 'done';
    try {
      await api.updateOrderStatus(token, o.id, next);
      setOrders(prev => prev.map(x => x.id === o.id ? { ...x, status: next } : x));
    } catch {}
  };

  const handleDelete = async (id: number) => {
    if (!token || !window.confirm(t('Обрисати ову наруџбину?'))) return;
    try {
      await api.deleteOrder(token, id);
      setOrders(prev => prev.filter(o => o.id !== id));
    } catch {}
  };

  if (!isAdmin) {
    return (
      <div className="news-page">
        <div className="state-block" role="alert" style={{ paddingTop: '80px' }}>
          <span>{t('Приступ дозвољен само администратору.')}</span>
          <Link to="/" className="state-retry">{t('Почетна')}</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="news-page orders-page">
      <div className="news-page-header">
        <h1 className="news-heading">{t('НАРУЏБИНЕ')}</h1>
        <div className="news-admin-toolbar">
          <Link to="/isporuke" className="btn-secondary">{t('← Производи')}</Link>
        </div>
      </div>

      {loading && (
        <div className="state-block" role="status" aria-live="polite">
          <div className="state-spinner" aria-hidden="true" />
          <span>{t('Учитавање...')}</span>
        </div>
      )}

      {error && !loading && (
        <div className="state-block state-error" role="alert">
          <span>{t(error)}</span>
          <button type="button" className="state-retry" onClick={loadOrders}>{t('Покушај поново')}</button>
        </div>
      )}

      {!loading && !error && orders.length === 0 && (
        <div className="state-block"><span>{t('Још нема наруџбина.')}</span></div>
      )}

      {!loading && !error && orders.length > 0 && (
        <div className="orders-list">
          {orders.map(o => (
            <article key={o.id} className={`order-card ${o.status === 'new' ? 'order-status-new' : 'order-status-done'}`}>
              <div className="order-card-head">
                <span className="order-card-product">{t(o.productName || '—')}</span>
                <span className={`order-badge ${o.status === 'new' ? 'badge-new' : 'badge-done'}`}>
                  {o.status === 'new' ? t('Ново') : t('Решено')}
                </span>
              </div>
              <div className="order-card-grid">
                <div><span className="order-k">{t('Купац')}</span><span className="order-v">{o.customerName}</span></div>
                <div><span className="order-k">{t('Телефон')}</span><span className="order-v">{o.phone}</span></div>
                {o.email && <div><span className="order-k">{t('Имејл')}</span><span className="order-v">{o.email}</span></div>}
                {o.address && <div><span className="order-k">{t('Адреса')}</span><span className="order-v">{o.address}</span></div>}
                <div><span className="order-k">{t('Количина')}</span><span className="order-v">{o.quantity}</span></div>
                <div><span className="order-k">{t('Датум')}</span><span className="order-v">{o.createdAt}</span></div>
              </div>
              {o.note && <p className="order-card-note"><span className="order-k">{t('Напомена')}:</span> {o.note}</p>}
              <div className="order-card-actions">
                <button className="post-edit-btn" onClick={() => toggleStatus(o)}>
                  {o.status === 'new' ? t('Означи решено ✓') : t('Врати на ново ↺')}
                </button>
                <button className="post-edit-btn gallery-delete-btn" onClick={() => handleDelete(o.id)}>
                  {t('Обриши ✕')}
                </button>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
};

export default OrdersAdminPage;
